import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import {
  Create,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { dataProvider } from 'src/providers/data-provider';

export enum SettingKeys {
  form_extract_prompt = 'form_extract_prompt',
  form_extract_prompt_2_1 = 'form_extract_prompt_2_1',
}

const validateJSON = (value: string) => {
  if (!value) return undefined;
  try {
    JSON.parse(value);
    return undefined;
  } catch (error) {
    return 'Invalid JSON format';
  }
};

interface DocumentCreateContentProps {
  selectedImage: any;
  setSelectedImage: any;
}

const DocumentCreateContent: React.FC<DocumentCreateContentProps> = ({ selectedImage, setSelectedImage }) => {
  const translate = useTranslate();
  const [imagePreview, setImagePreview] = useState<any>(null);

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setSelectedImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Image Upload */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translate('form_submission.create.image_upload')}
            </Typography>
            <Box
              sx={{
                width: '400px',
                height: '192px',
                bgcolor: 'grey.100',
                borderRadius: 1,
                border: 1,
                borderColor: 'grey.300',
                mb: 4,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {imagePreview ? (
                <Box sx={{ position: 'relative', height: '100%' }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Document preview"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <label>
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          color: 'primary.main',
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            bgcolor: 'grey.50',
                          },
                        }}
                      >
                        <CameraAltIcon />
                        <Typography>{translate('simple.reselect')}</Typography>
                        <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                      </Box>
                    </label>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CameraAltIcon
                    sx={{
                      color: 'grey.400',
                      fontSize: 48,
                      mb: 1,
                    }}
                  />
                  <label>
                    <Box
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'common.white',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      }}
                    >
                      <Typography>{translate('simple.select_a_photo')}</Typography>
                      <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                    </Box>
                  </label>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Form Fields */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translate('form_submission.create.form_info')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput
                  source="name"
                  validate={required()}
                  fullWidth
                  label={translate('form_submission.fields.name')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  source="expected_data"
                  multiline
                  rows={4}
                  fullWidth
                  label={translate('form_submission.fields.expected_data')}
                  helperText="JSON value"
                  validate={validateJSON}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  source="note"
                  multiline
                  rows={2}
                  fullWidth
                  label={translate('form_submission.fields.note')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export const DocumentCreate = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const uploadDocumentImage = async (file: any) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const authParams = localStorage?.getItem('auth') || '';
      const { access_token } = JSON.parse(authParams);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/form/upload-template-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const data = await response.json();
      return data.image_key;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);

      let imageKey = null;
      if (selectedImage) {
        imageKey = await uploadDocumentImage(selectedImage);
      }

      if (!imageKey) {
        notify('simple.image_is_required', { type: 'error' });
        return;
      }

      const gqlQuery = `
        mutation request_admin_create_form_submission($params: request_admin_create_form_submission_input!) {
          request_admin_create_form_submission(params: $params) {
            error
          }
        }
      `;

      const variables = {
        params: {
          name: formData.name,
          imageKey: imageKey,
          expected_data: formData.expected_data ? JSON.parse(formData.expected_data) : null,
          note: formData.note || null,
        },
      };

      const response = await dataProvider.create('form_submission', {
        data: formData,
        meta: {
          gqlQuery,
          variables,
        },
      });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      notify(translate('form_submission.notification.created_success'), { type: 'success' });
      redirect('list', 'form_submission');
    } catch (error) {
      console.error('Create error:', error);
      notify(translate('form_submission.notification.created_error'), { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create title={translate('form_submission.create.title')}>
      <SimpleForm
        onSubmit={handleSubmit}
        toolbar={
          <Toolbar>
            <SaveButton disabled={isSubmitting} />
          </Toolbar>
        }
      >
        <DocumentCreateContent selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
      </SimpleForm>
    </Create>
  );
};

export default DocumentCreate;
